import posthog from "posthog-js";
import { CONSENT_STORAGE_KEY, hasAnalyticsConsent } from "@/lib/consent";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com";

// Consent gate. PostHog sets analytics cookies and autocaptures, which Quebec
// Law 25 (Bill 64) and PIPEDA treat as non-essential tracking that requires
// opt-in. We never init the SDK until the visitor accepts in CookieConsent.
// The gate lives in src/lib/consent.ts so every tracker shares one source of
// truth; re-exported here for the existing CookieConsent / test imports.
export { CONSENT_STORAGE_KEY };

/** True only when the visitor has explicitly accepted analytics cookies. */
export const analyticsConsentGranted = hasAnalyticsConsent;

// Tracks whether posthog.init() has actually run. Every downstream call
// (identify, capture, reset) is a no-op until it has, so events fired before
// consent are dropped rather than buffered.
let initialized = false;

export function initPostHog() {
  if (!POSTHOG_KEY) return;
  // Do not initialize without consent. main.tsx re-calls this when the user
  // accepts, so a late opt-in still starts analytics within the same session.
  if (!analyticsConsentGranted()) return;
  if (initialized) return;
  initialized = true;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    capture_exceptions: true,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    // -----------------------------------------------------------------------
    // Session-replay PII masking.
    // Replays record authenticated dashboard sessions, which show carrier /
    // broker contact info, deal terms, and billing data. Decision:
    //   - maskAllInputs: every <input>/<textarea>/<select> value is masked at
    //     the recorder, so typed PII (emails, MC/DOT numbers, rates, card
    //     fields) never leaves the browser.
    //   - maskTextSelector: we do NOT use "*" — that would blind replays and
    //     make them useless for debugging. There was no existing masking
    //     convention in this codebase, so this establishes one: rendered
    //     (non-input) text is masked when it is inherently PII —
    //     mailto:/tel: links — or when a component opts in with the
    //     `data-ph-mask` attribute. Add data-ph-mask to any element that
    //     renders emails, phone numbers, addresses, or financial figures.
    //   - PostHog defaults also apply: elements with class "ph-no-capture"
    //     are excluded from the recording entirely (use for whole PII-heavy
    //     blocks, e.g. billing cards), and class "ph-mask" masks text
    //     (rrweb's default maskTextClass).
    // -----------------------------------------------------------------------
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask], a[href^="mailto:"], a[href^="tel:"]',
    },
    // -----------------------------------------------------------------------
    // CSP compatibility: surveys, web experiments, and site apps are PostHog
    // features that fetch + eval() remote scripts from us-assets.i.posthog.com.
    // Our production CSP (set in Cloudflare, see the CSP note in index.html)
    // deliberately has no 'unsafe-eval' in script-src, so leaving these on
    // throws "EvalError: Refused to evaluate..." in Sentry. We disable the
    // features rather than weaken the CSP — none of them are used.
    // -----------------------------------------------------------------------
    disable_surveys: true,
    disable_web_experiments: true,
    opt_in_site_apps: false,
    sanitize_properties: (properties) => {
      // Preserve pathname so funnels by route work, but strip query params and
      // hash where oauth tokens, search filters, and PII can leak.
      const stripUrl = (v: unknown): string | undefined => {
        if (typeof v !== "string") return undefined;
        try {
          const u = new URL(v, typeof window !== "undefined" ? window.location.origin : "https://app");
          return u.origin + u.pathname;
        } catch {
          return v;
        }
      };
      if (properties["$current_url"]) {
        const cleaned = stripUrl(properties["$current_url"]);
        if (cleaned) properties["$current_url"] = cleaned;
      }
      if (properties["$referrer"]) {
        const cleaned = stripUrl(properties["$referrer"]);
        if (cleaned) properties["$referrer"] = cleaned;
      }
      return properties;
    },
  });
}

/**
 * Identify authenticated user · ID only, no email (matches Sentry policy —
 * setSentryUser drops email on purpose, SOC 2 MOD-2; same stance here).
 *
 * `isTestTenant` mirrors companies.is_test_tenant so the PostHog project's
 * test-account filter can exclude internal/QA tenants from every insight.
 * Measured 2026-08-09: without it, qa-dispatcher was the heaviest "user" in
 * prod (52 scans/30d) and user counts ran ~2.5x high. Only ever pass a value
 * read from the companies row — never infer it client-side.
 */
export interface IdentifyUserInput {
  id: string;
  companyId?: string;
  role?: string;
  isTestTenant?: boolean;
}

/** Pure mapping — exported for tests. */
export function identifyProps(user: IdentifyUserInput): Record<string, unknown> {
  return {
    company_id: user.companyId,
    role: user.role,
    // Omit entirely when unknown (the eager pre-resolve identify) so the later
    // enriched identify is what sets it — identify merges person properties,
    // and an explicit key here would overwrite a known value with undefined.
    ...(user.isTestTenant === undefined ? {} : { is_test_tenant: user.isTestTenant }),
  };
}

export function identifyUser(user: IdentifyUserInput) {
  if (!POSTHOG_KEY || !initialized) return;
  posthog.identify(user.id, identifyProps(user));
}

/** Reset on logout */
export function resetPostHog() {
  if (!POSTHOG_KEY || !initialized) return;
  posthog.reset();
}

/** Track a custom event */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY || !initialized) return;
  posthog.capture(event, properties);
}

export { posthog };
